import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

/** Where a blocked navigation was heading, so it can be completed later. */
export type PendingNavigation = { kind: 'to'; to: string } | { kind: 'back' };

export type NavigationGuard = {
  /** Non-null while a navigation is waiting on the operator's decision. */
  pending: PendingNavigation | null;
  /**
   * Route a link through the guard instead of navigating directly.
   *
   * When nothing is at stake this navigates immediately, so a guarded link is
   * safe to use unconditionally.
   */
  request: (to: string) => void;
  /** Abandon the navigation and stay, draft intact. */
  cancel: () => void;
  /** Give up the draft and complete the navigation that was blocked. */
  confirm: () => void;
};

/**
 * Marks the duplicate history entry the guard pushes, so arming is idempotent
 * and cannot stack two duplicates (React's StrictMode runs effects twice).
 */
const SENTINEL = '__redirectorGuard';

function currentHistoryState(): Record<string, unknown> | null {
  return window.history.state as Record<string, unknown> | null;
}

/**
 * Pushes a duplicate of the current history entry, unless one is already there.
 *
 * React Router's own `idx` bookkeeping is carried over rather than replaced, so
 * the duplicate is indistinguishable to the router and its index does not shift.
 */
function arm(): void {
  const state = currentHistoryState();
  if (state?.[SENTINEL] === true) return;
  window.history.pushState({ ...state, [SENTINEL]: true }, '', window.location.href);
}

/**
 * Warns before a navigation abandons unsaved work.
 *
 * `useBlocker` would be the obvious tool and is deliberately not used: it is
 * only available inside a data router, and this app mounts a `BrowserRouter`
 * with a `<Routes>` tree. So the two ways off this page are intercepted
 * directly.
 *
 * In-app links call `request` and are simply not followed until the operator
 * decides. The browser Back button cannot be cancelled once it has fired, so
 * instead a duplicate of the current entry is pushed while the draft is at
 * stake: Back consumes the duplicate, which leaves the URL unchanged, so this
 * page is still mounted and there is still something to warn about when the
 * `popstate` arrives. Confirming then steps back over both entries.
 */
export function useNavigationGuard(active: boolean): NavigationGuard {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingNavigation | null>(null);

  // Read inside a callback that must not be re-created on every keystroke of
  // the draft, so the latest value is taken from a ref rather than closed over.
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!active) return;

    arm();

    const onPopState = () => {
      // The duplicate has been consumed. Re-arm before asking, so cancelling
      // leaves the trap exactly as it was and a second Back press still warns.
      arm();
      setPending({ kind: 'back' });
    };

    // A reload or a closed tab is not a route change, so nothing in React can
    // warn about it; the browser's own prompt is the only thing that can.
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('popstate', onPopState);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [active]);

  const request = useCallback(
    (to: string) => {
      if (!activeRef.current) {
        void navigate(to);
        return;
      }
      setPending({ kind: 'to', to });
    },
    [navigate],
  );

  const cancel = useCallback(() => setPending(null), []);

  const confirm = useCallback(() => {
    const target = pending;
    setPending(null);
    if (target === null) return;

    if (target.kind === 'to') {
      /*
       * `replace`, because the entry being replaced is the duplicate this guard
       * pushed. Pushing instead would leave that artefact in the stack, where
       * it would read as a Back press that appears to do nothing.
       */
      void navigate(target.to, { replace: true });
      return;
    }

    // The stack is [… previous, this page, duplicate] and we are standing on
    // the duplicate, so leaving takes two steps rather than one.
    window.history.go(-2);
  }, [navigate, pending]);

  return { pending, request, cancel, confirm };
}
